import type { DataCache, RequirementWithStatus } from "@uoplan/core";
import {
  courseMatchesFilters,
  getEffectiveSchedule,
  groupTokenPrefix,
  isGroupToken,
  isHonoursProject,
  makeGroupToken,
  normalizeCourseCode,
  subjectPrefix,
} from "@uoplan/core";
import {
  isElectiveRequirementType,
  isWithinElectiveLevelCap,
  virtualScheduleFilterApplies,
} from "@uoplan/store/electiveEligibility";
import { tr } from "../../i18n";
import { courseMatchesElectiveLevelBuckets } from "@uoplan/store/requirements/selectionUtils";
export {
  appendCourseDedupedByNorm,
  applyOptionSelections,
  collectRequirementIdsWithCandidateCourse,
  pruneOptionSelectionsForClear,
  resolveRequirementIdsForScheduleCourse,
} from "@uoplan/store/requirements/selectionUtils";

function normalizeTitleForCompare(title: string | undefined): string {
  return (title ?? "").trim().replaceAll(/\s+/g, " ").toLowerCase();
}

/**
 * UX helper: reduce "dropdown inside dropdown" when a node is just a wrapper
 * around a single child. Matches {@link RequirementNode} behavior.
 */
export function simplifySingleChildChain(node: RequirementWithStatus): {
  node: RequirementWithStatus;
  autoExpanded: boolean;
} {
  let current = node;
  let changed = false;

  while (
    current.requirementId == null &&
    current.options &&
    current.options.length === 1 &&
    current.options[0] &&
    (current.options[0].options?.length ?? 0) > 0 &&
    (() => {
      const parentT = normalizeTitleForCompare(current.title);
      const childT = normalizeTitleForCompare(current.options[0].title);
      const parentGeneric = parentT === "" || parentT === "or";
      const childGeneric = childT === "" || childT === "or";
      return parentGeneric || childGeneric || parentT === childT;
    })()
  ) {
    const child = current.options[0];
    current = {
      ...child,
      title: (current.title ?? "").trim() ? current.title : child.title,
      code: current.code ?? child.code,
    };
    changed = true;
  }

  return { node: current, autoExpanded: changed };
}

/**
 * Returns true if the node (or any non-complete descendant) is an
 * or_group/options_group with a requirementId, i.e. it needs user selection.
 */
export function nodeHasOptionGroups(node: RequirementWithStatus): boolean {
  if (node.complete) return false;
  if ((node.type === "or_group" || node.type === "options_group") && node.requirementId != null) {
    return true;
  }
  return node.options?.some(nodeHasOptionGroups) ?? false;
}

/** One-line hint under an option in the Options step (credits, pool size, nested groups). */
export function getOptionSecondarySummaryLine(opt: RequirementWithStatus): string | null {
  const parts: string[] = [];
  const credits = opt.creditsNeeded ?? 0;
  if (credits > 0) {
    parts.push(`${credits} credit${credits !== 1 ? "s" : ""} required`);
  }
  const n = opt.candidateCourses?.length ?? 0;
  if (n > 0) {
    parts.push(`${n} possible course${n !== 1 ? "s" : ""}`);
  }
  if (nodeHasOptionGroups(opt)) {
    parts.push("Further choices below");
  }
  return parts.length > 0 ? parts.join(" · ") : null;
}

/**
 * Returns true if any or_group / options_group in the tree still needs a
 * selection from the user. Follows selected branches for nested groups so
 * that only currently-relevant unresolved groups are counted.
 */
export function hasMissingOptionSelections(
  nodes: RequirementWithStatus[],
  selectedOptions: Record<string, number>,
): boolean {
  for (const node of nodes) {
    if (node.complete) continue;
    const isOptionType = node.type === "or_group" || node.type === "options_group";
    if (isOptionType && node.requirementId != null) {
      const idx = selectedOptions[node.requirementId];
      const options = node.options ?? [];
      if (idx == null || idx < 0 || idx >= options.length) return true;
      const child = options[idx];
      if (child && hasMissingOptionSelections([child], selectedOptions)) return true;
    } else if (node.options?.length && hasMissingOptionSelections(node.options, selectedOptions)) {
      return true;
    }
  }
  return false;
}

/**
 * True when a node carries no displayable requirement of its own — a pure
 * structural shell whose only purpose is to group children. Used to decide
 * whether a wrapper that has been emptied by pruning should also be dropped.
 */
function isEmptyableStructuralWrapper(node: RequirementWithStatus): boolean {
  if (
    node.type !== "and" &&
    node.type !== "pick" &&
    node.type !== "group" &&
    node.type !== "section"
  ) {
    return false;
  }
  if (node.requirementId != null) return false;
  if ((node.candidateCourses?.length ?? 0) > 0) return false;
  if ((node.creditsNeeded ?? 0) > 0) return false;
  if (node.complete) return false;
  return true;
}

/**
 * Display-only pass for Assign / Constrain: removes `or_group` / `options_group`
 * nodes the user has not yet resolved (they have a `requirementId` and are not
 * complete) so they render nothing — option selection belongs to the Program
 * options step. Complete option groups are kept (they show a satisfied state),
 * and groups without a `requirementId` are left untouched. Structural wrappers
 * (`and` / `pick` / `group` / `section`) emptied by pruning are dropped so no
 * empty cards remain.
 *
 * Must run AFTER {@link applyOptionSelections}. Never mutate / replace
 * `applyOptionSelections` with this — that function is also used by non-display
 * logic (course swap) that relies on unresolved branches staying searchable.
 */
export function pruneUnresolvedOptionGroups(
  nodes: RequirementWithStatus[],
): RequirementWithStatus[] {
  const out: RequirementWithStatus[] = [];
  for (const node of nodes) {
    const isOptionType = node.type === "or_group" || node.type === "options_group";
    if (isOptionType && node.requirementId != null && !node.complete) {
      continue;
    }

    if (node.options?.length) {
      const prunedOptions = pruneUnresolvedOptionGroups(node.options);
      if (prunedOptions.length === 0 && isEmptyableStructuralWrapper(node)) {
        continue;
      }
      out.push({ ...node, options: prunedOptions });
    } else {
      out.push(node);
    }
  }
  return out;
}

/** True if normalized code appears in any of the per-requirement course lists. */
export function isCourseInPerRequirementMaps(
  courseNorm: string,
  ...maps: Record<string, string[]>[]
): boolean {
  for (const map of maps) {
    for (const codes of Object.values(map)) {
      if (codes.some((c) => normalizeCourseCode(c) === courseNorm)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Adjust a requirement tree node to reflect manual course assignments from the
 * Assign step (`selectedPerRequirement`). For each node with a requirementId:
 * - If assigned credits >= creditsNeeded → mark complete, zero out creditsNeeded
 * - If partially assigned → reduce creditsNeeded by assigned credits
 * Children are adjusted recursively; an and_group is marked complete when all
 * its children become complete.
 */
export function adjustNodeForAssignments(
  node: RequirementWithStatus,
  selectedPerRequirement: Record<string, string[]>,
  cache: DataCache | null,
): RequirementWithStatus {
  const assigned = node.requirementId ? (selectedPerRequirement[node.requirementId] ?? []) : [];
  const assignedCredits = assigned.reduce(
    (sum, code) => sum + (cache?.getCourse(normalizeCourseCode(code))?.credits ?? 3),
    0,
  );
  const origCredits = node.creditsNeeded ?? 0;

  if (origCredits > 0 && assignedCredits >= origCredits) {
    return { ...node, complete: true, creditsNeeded: 0, satisfiedBy: assigned };
  }

  const newCredits =
    origCredits > 0 && assignedCredits > 0 ? origCredits - assignedCredits : origCredits;

  const adjustedOptions = node.options?.map((child) =>
    adjustNodeForAssignments(child, selectedPerRequirement, cache),
  );

  const isOrLike = node.type === "or_group" || node.type === "options_group";
  const anyChildComplete =
    isOrLike && adjustedOptions != null && adjustedOptions.some((c) => c.complete);
  const allChildrenComplete =
    !isOrLike &&
    adjustedOptions != null &&
    adjustedOptions.length > 0 &&
    adjustedOptions.every((c) => c.complete);

  return {
    ...node,
    creditsNeeded: newCredits,
    ...(anyChildComplete || allChildrenComplete ? { complete: true } : {}),
    ...(adjustedOptions ? { options: adjustedOptions } : {}),
  };
}

/**
 * Number of top-level program requirements (roots of `requirementTreeWithStatus`)
 * that are satisfied: engine completion and/or enough assigned credits per
 * {@link adjustNodeForAssignments}. Matches the badge denominator
 * (`roots.length`).
 */
export function countSatisfiedTopLevelRoots(
  roots: RequirementWithStatus[],
  selectedPerRequirement: Record<string, string[]>,
  cache: DataCache | null,
): number {
  let n = 0;
  for (const root of roots) {
    if (adjustNodeForAssignments(root, selectedPerRequirement, cache).complete) {
      n++;
    }
  }
  return n;
}

function isCourseExplicitlyMentioned(node: RequirementWithStatus, code: string): boolean {
  if (node.type === "course" || node.type === "or_course") {
    return !!node.candidateCourses?.includes(code);
  }
  if (node.options) {
    return node.options.some((child) => isCourseExplicitlyMentioned(child, code));
  }
  return false;
}

/** Matches {@link RequirementNode} MultiSelect filtering (Assign / Constrain steps). */
interface ConstrainMultiSelectContext {
  cache: DataCache | null;
  completedCourses: Set<string>;
  prereqEligible: Set<string>;
  levelBuckets: ("undergrad" | "grad")[];
  languageBuckets: ("en" | "fr" | "other")[];
  electiveLevelBuckets: number[];
  unassignedCompletedSetNormalized: Set<string>;
  allAssignedCoursesNormalized: Set<string>;
  includeClosedComponents: boolean;
  virtualSectionsOnly: boolean;
  /** Constrain-step selections, used for virtual-only exemption in AssignStep UI. */
  constrainedPerRequirement?: Record<string, string[]>;
  completedOnly: boolean;
}

interface ConstrainMultiSelectOption {
  value: string;
  label: string;
  disabled: boolean;
}

/**
 * Builds MultiSelect `data` and selected values for a requirement node — same
 * rules as {@link RequirementNode}'s course picker.
 */
export function getConstrainMultiSelectOptions(
  node: RequirementWithStatus,
  selectedPerRequirement: Record<string, string[]>,
  ctx: ConstrainMultiSelectContext,
): { selectedForDisplay: string[]; options: ConstrainMultiSelectOption[] } {
  const selected = node.requirementId ? (selectedPerRequirement[node.requirementId] ?? []) : [];

  const explicitExemptNormalized = new Set<string>(
    [
      ...(node.requirementId ? (ctx.constrainedPerRequirement?.[node.requirementId] ?? []) : []),
      ...selected,
    ]
      .filter((c) => !isGroupToken(c))
      .map((c) => normalizeCourseCode(c)),
  );

  const isElectiveType = isElectiveRequirementType(node.type);
  const isElectiveWithExclusions = isElectiveType && (node.excluded_disciplines?.length ?? 0) > 0;

  const isCompletedCourse = (code: string): boolean => {
    const norm = normalizeCourseCode(code);
    const canonical = ctx.cache?.getCourse(norm)?.code ?? code;
    return ctx.completedCourses.has(canonical) || ctx.completedCourses.has(norm);
  };

  const filtered =
    node.candidateCourses
      ?.filter((c) => ctx.prereqEligible.has(c))
      .filter((c) => {
        const isCompleted =
          isCompletedCourse(c) || ctx.unassignedCompletedSetNormalized.has(normalizeCourseCode(c));
        if (ctx.completedOnly) return isCompleted;
        if (isCompleted) return false;
        if (
          !courseMatchesFilters(c, {
            levels: ctx.levelBuckets,
            languageBuckets: ctx.languageBuckets,
          })
        ) {
          return false;
        }
        if (
          ctx.cache &&
          !getEffectiveSchedule(
            ctx.cache,
            c,
            ctx.includeClosedComponents,
            virtualScheduleFilterApplies(
              ctx.virtualSectionsOnly,
              node.type,
              c,
              explicitExemptNormalized,
            ),
          )
        ) {
          if (isHonoursProject(c, ctx.cache) && isCourseExplicitlyMentioned(node, c)) {
            return true;
          }
          return false;
        }
        if (isElectiveType && !isWithinElectiveLevelCap(c)) {
          return false;
        }
        if (
          isElectiveWithExclusions &&
          !courseMatchesElectiveLevelBuckets(c, ctx.electiveLevelBuckets)
        ) {
          return false;
        }
        return true;
      }) ?? [];

  const selectedForDisplay = selected.map(
    (c) => ctx.cache?.getCourse(normalizeCourseCode(c))?.code ?? c,
  );

  const normalizedSeen = new Set<string>();
  const available: string[] = [];
  for (const c of selectedForDisplay) {
    if (isGroupToken(c)) continue;
    const norm = normalizeCourseCode(c);
    if (normalizedSeen.has(norm)) continue;
    normalizedSeen.add(norm);
    available.push(ctx.cache?.getCourse(norm)?.code ?? c);
  }
  for (const c of filtered) {
    const norm = normalizeCourseCode(c);
    if (normalizedSeen.has(norm)) continue;
    normalizedSeen.add(norm);
    available.push(ctx.cache?.getCourse(norm)?.code ?? c);
  }

  const availableSorted = [...available].sort((a, b) => {
    const aUn = ctx.unassignedCompletedSetNormalized.has(normalizeCourseCode(a));
    const bUn = ctx.unassignedCompletedSetNormalized.has(normalizeCourseCode(b));
    if (aUn && !bUn) return -1;
    if (!aUn && bUn) return 1;
    return 0;
  });

  // Prefixes covered by currently-selected group tokens
  const selectedGroupPrefixes = new Set<string>(
    selected.filter(isGroupToken).map(groupTokenPrefix),
  );

  // Count courses per prefix among available (real codes only)
  const prefixCounts = new Map<string, number>();
  for (const code of available) {
    const pfx = subjectPrefix(code);
    prefixCounts.set(pfx, (prefixCounts.get(pfx) ?? 0) + 1);
  }

  // Group options for prefixes with ≥ 2 courses, sorted alphabetically
  const groupOptions: ConstrainMultiSelectOption[] = [...prefixCounts.entries()]
    .filter(([, count]) => count >= 2) // single-course prefixes offer no real choice as a group
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([pfx]) => ({
      value: makeGroupToken(pfx),
      label: tr("requirementNode.anyCourseGroupAdd", { prefix: pfx }),
      disabled: false,
    }));

  // Individual course options — disable if prefix covered by a selected group.
  // Label is the bare code so selected pills show only the code; the dropdown adds
  // the title via the MultiSelect's renderOption.
  const courseOptions = availableSorted.map((code) => {
    const norm = normalizeCourseCode(code);
    const usedElsewhere =
      ctx.allAssignedCoursesNormalized.has(norm) && !selectedForDisplay.includes(code);
    const coveredByGroup = selectedGroupPrefixes.has(subjectPrefix(code));
    return { value: code, label: code, disabled: usedElsewhere || coveredByGroup };
  });

  let options = [...groupOptions, ...courseOptions];
  const optionValues = new Set(options.map((o) => o.value));
  for (const s of selected) {
    if (!isGroupToken(s) || optionValues.has(s)) continue;
    optionValues.add(s);
    options = [
      {
        value: s,
        label: tr("requirementNode.anyCourseGroup", {
          prefix: groupTokenPrefix(s),
        }),
        disabled: false,
      },
      ...options,
    ];
  }

  return { selectedForDisplay, options };
}

/**
 * True when this subtree has no “main flow” Constrain UI: no unresolved option
 * group, and every course MultiSelect’s {@link getConstrainMultiSelectOptions}
 * `options` array is empty (same pool as the dropdown: prereqs, term offering,
 * filters, etc.).
 *
 * Nodes without a `requirementId` do not render a MultiSelect — they count as
 * vacuously “empty” so e.g. a `pick` hides when **all** descendants with
 * pickers have empty pools.
 */
function subtreeHasOnlyEmptyConstrainDropdowns(
  node: RequirementWithStatus,
  constrainedPerRequirement: Record<string, string[]>,
  ctx: ConstrainMultiSelectContext,
): boolean {
  if (node.complete) return true;
  if (node.type === "section") return true;

  const isOptionType = node.type === "or_group" || node.type === "options_group";
  if (isOptionType && node.requirementId != null) {
    const idx = node.satisfiedOptionIndex;
    const nOpts = node.options?.length ?? 0;
    if (idx == null || nOpts === 0 || idx < 0 || idx >= nOpts) return false;
    const child = node.options![idx];
    return subtreeHasOnlyEmptyConstrainDropdowns(child, constrainedPerRequirement, ctx);
  }

  if (node.options?.length) {
    const structural =
      node.type === "and" ||
      node.type === "pick" ||
      node.type === "group" ||
      (isOptionType && node.requirementId == null);
    if (structural) {
      const childrenAllEmpty = node.options.every((child) =>
        subtreeHasOnlyEmptyConstrainDropdowns(child, constrainedPerRequirement, ctx),
      );
      if (!childrenAllEmpty) return false;
      // pick/group: hide the whole block only when every child is empty **and**
      // any parent-card MultiSelect (requirementId on this node) is empty too.
      if ((node.type === "pick" || node.type === "group") && node.requirementId != null) {
        const { options } = getConstrainMultiSelectOptions(node, constrainedPerRequirement, ctx);
        return options.length === 0;
      }
      return true;
    }
  }

  if (node.requirementId != null) {
    const { options } = getConstrainMultiSelectOptions(node, constrainedPerRequirement, ctx);
    return options.length === 0;
  }

  // No Constrain MultiSelect on this node — does not block “all children empty”.
  return true;
}

interface PartitionedConstrainRoot {
  node: RequirementWithStatus;
  /** Index of this root in the original `incompleteRoots` array (stable keys). */
  rootIndex: number;
}

/** Splits roots by whether every constrain MultiSelect would show an empty dropdown. */
export function partitionIncompleteConstrainRoots(
  incompleteRoots: RequirementWithStatus[],
  constrainedPerRequirement: Record<string, string[]>,
  ctx: ConstrainMultiSelectContext,
): { primary: PartitionedConstrainRoot[]; collapsed: PartitionedConstrainRoot[] } {
  const primary: PartitionedConstrainRoot[] = [];
  const collapsed: PartitionedConstrainRoot[] = [];
  for (let i = 0; i < incompleteRoots.length; i++) {
    const root = incompleteRoots[i];
    const entry: PartitionedConstrainRoot = { node: root, rootIndex: i };
    if (subtreeHasOnlyEmptyConstrainDropdowns(root, constrainedPerRequirement, ctx)) {
      collapsed.push(entry);
    } else {
      primary.push(entry);
    }
  }
  return { primary, collapsed };
}
